<style scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}
/* 地图容器子元素（Swqyun SDK 会在此 div 内创建 canvas）
   注意：不使用 !important，以便 JS 可以通过内联样式精确控制尺寸 */
.map-container > div:first-child {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.map-tools {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  background: rgba(255,255,255,0.9);
  padding: 8px;
  border-radius: 4px;
}
</style>

<template>
  <div class="map-container">
    <div :id="containerId"></div>
    <div v-if="editable" class="map-tools">
      <el-radio-group v-model="innerDrawType" size="small">
        <el-radio-button label="point" @click.native="onDrawTypeClick">点</el-radio-button>
        <el-radio-button label="line" @click.native="onDrawTypeClick">线</el-radio-button>
        <el-radio-button label="area" @click.native="onDrawTypeClick">面</el-radio-button>
      </el-radio-group>
      <el-button size="small" @click.stop="clearDraw" style="margin-left: 8px;">清除</el-button>
      <el-button v-if="isDrawing" size="small" type="warning" @click.stop="handleStopDraw">结束绘制</el-button>
    </div>

  </div>
</template>

<script>
export default {
  name: "SwqyunMap",
  props: {
    sceneUrl: { type: String, required: true },
    sessionKey: { type: String, required: true },
    appId: { type: String, required: true },
    editable: { type: Boolean, default: false },
    drawType: { type: String, default: null },
    vectors: { type: Array, default: () => [] },
    markers: { type: Array, default: () => [] },
    autoFly: { type: Boolean, default: false },
    scene: { type: String, default: "task" },
    extraScenes: { type: Array, default: () => [] }
  },
  data() {
    return {
      isDrawing: false,
      innerDrawType: this.drawType,
      _vectorType: "point",
      keyList: [],
      keyIndex: 1,
      keyPrefix: "drawKey",
      containerId: "swqyunMapContainer-" + Math.random().toString(36).substr(2, 9),
      pointList: [],
      currentHeight: 100,
      globalHeight: 100,
      markerMap: {},
      vectorKeyMap: {},
      isInitializing: false,
      _entities: [],
      _markerEntities: [],
      _previewEntities: []
    };
  },
  created() {
    this._app = null;
    this._canvasEl = null;
    this._onContextMenu = null;
    this._initTimer = null;
    this._startDrawTimer = null;
    this._currentPrimitive = null;
    this._drawUpdatedHandler = null;
    this._cameraChangedHandler = null;
  },
  watch: {
    sceneUrl(val, oldVal) {
      if (val && this.sessionKey && this.appId) {
        if (!this._app) {
          this.delayInitMap();
        } else if (oldVal && oldVal !== val) {
          this.reloadScene();
        }
      }
    },
    sessionKey(val) {
      if (val && this.sceneUrl && this.appId && !this._app) {
        this.delayInitMap();
      }
    },
    appId(val) {
      if (val && this.sceneUrl && this.sessionKey && !this._app) {
        this.delayInitMap();
      }
    },
    drawType(val) {
      this.innerDrawType = val;
      if (val && this.editable && this._app) {
        this.startDraw();
      }
    },
    extraScenes: {
      deep: true,
      handler() {
        if (this._app) {
          this.importExtraScenes();
        }
      }
    },
    editable(val) {
      if (!val && this.isDrawing) {
        this.stopDraw();
        this.reDrawVectors();
        this.drawMarkers();
      } else if (val && this._app) {
        this.reDrawVectors();
        this.drawMarkers();
        // 进入编辑模式时，如果已指定绘制类型，自动启动绘制
        if (this.innerDrawType) {
          this.startDraw();
        }
      }
    },
    vectors: {
      deep: true,
      handler() {
        if (this._app) {
          this.reDrawVectors();
        }
      }
    },
    markers: {
      deep: true,
      handler() {
        if (this._app) {
          this.drawMarkers();
        }
      }
    }
  },
  mounted() {
    if (this.sceneUrl && this.sessionKey && this.appId) {
      this.delayInitMap();
    }

  },
  beforeDestroy() {
    if (this._initTimer) clearTimeout(this._initTimer);
    if (this._coreWaitTimer) clearInterval(this._coreWaitTimer);
    if (this._startDrawTimer) clearTimeout(this._startDrawTimer);
    if (this._currentPrimitive && this._currentPrimitive.drawUpdatedEvent && this._drawUpdatedHandler) {
      try { this._currentPrimitive.drawUpdatedEvent.removeEventListener(this._drawUpdatedHandler); } catch(e) {}
    }
    // 移除 camera.changed 监听器，避免内存泄漏
    if (this._cameraChangedHandler) {
      if (this._app) {
        try {
          const viewer = this._app._viewer || this._app;
          if (viewer && viewer.camera && viewer.camera.changed) {
            viewer.camera.changed.removeEventListener(this._cameraChangedHandler);
          }
        } catch (e) {}
      }
      this._cameraChangedHandler = null;
    }
    // 移除 scene.postRender 监听器
    if (this._postRenderHandler) {
      if (this._app) {
        try {
          const viewer = this._app._viewer || this._app;
          if (viewer && viewer.scene && viewer.scene.postRender) {
            viewer.scene.postRender.removeEventListener(this._postRenderHandler);
          }
        } catch (e) {}
      }
      this._postRenderHandler = null;
    }
    window.removeEventListener("resize", this.doResize);
    if (this._canvasEl) {
      this._canvasEl.removeEventListener("contextmenu", this._onContextMenu);
      this._canvasEl = null;
    }
    if (this._app && this._app.destroy) {
      try { this._app.destroy(); } catch(e) {}
    }
  },
  methods: {
    delayInitMap() {
      if (this._app || this.isInitializing) return;
      // 等待 ES Module 版本的 Core / AppEventType 就绪
      if (!window.Swqyun || !window.Swqyun.Core) {
        if (!this._coreWaitTimer) {
          this._coreWaitTimer = setInterval(() => {
            if (window.Swqyun && window.Swqyun.Core) {
              clearInterval(this._coreWaitTimer);
              this._coreWaitTimer = null;
              this.delayInitMap();
            }
          }, 100);
          setTimeout(() => {
            if (this._coreWaitTimer) {
              clearInterval(this._coreWaitTimer);
              this._coreWaitTimer = null;
              console.error("[SwqyunMap] Swqyun.Core not loaded after timeout");
            }
          }, 10000);
        }
        return;
      }
      this.isInitializing = true;
      if (this._initTimer) clearTimeout(this._initTimer);
      this.$nextTick(() => {
        this._initTimer = setTimeout(() => {
          const container = document.getElementById(this.containerId);
          if (container) {
            const parent = container.parentElement;
            let rect = parent ? parent.getBoundingClientRect() : { width: 0, height: 0 };
            let w = rect.width;
            let h = rect.height;
            let cur = parent;
            while (h <= 0 && cur) {
              rect = cur.getBoundingClientRect();
              h = rect.height;
              w = rect.width;
              cur = cur.parentElement;
            }
            if (h <= 0) {
              h = window.innerHeight;
              w = window.innerWidth;
            }
            container.style.width = w + "px";
            container.style.height = h + "px";
          }
          this.initMap();
        }, 500);
      });
    },
    reloadScene() {
      if (!this._app) return;
      try {
        if (this._app.destroy) {
          this._app.destroy();
        }
      } catch (e) {}
      this._app = null;
      if (this._canvasEl) {
        this._canvasEl.removeEventListener("contextmenu", this._onContextMenu);
        this._canvasEl = null;
      }
      window.removeEventListener("resize", this.doResize);
      this.keyList = [];
      this.pointList = [];
      this.markerMap = {};
      this.delayInitMap();
    },
    doResize() {
      try {
        const container = document.getElementById(this.containerId);
        if (!container) return;
        const parent = container.parentElement;
        let rect = parent ? parent.getBoundingClientRect() : { width: 0, height: 0 };
        let w = rect.width;
        let h = rect.height;
        let cur = parent;
        while (h <= 0 && cur) {
          rect = cur.getBoundingClientRect();
          h = rect.height;
          w = rect.width;
          cur = cur.parentElement;
        }
        if (h <= 0) {
          h = window.innerHeight;
          w = window.innerWidth;
        }
        if (w <= 0 || h <= 0) return;
        // 同步容器尺寸为当前实际可用空间（使用明确像素值，避免 100% 在某些嵌套布局下失效）
        container.style.width = w + "px";
        container.style.height = h + "px";
        // 同时撑满 Cesium 内部 viewer 容器
        const viewerEl = container.querySelector(".cesium-viewer");
        if (viewerEl) {
          viewerEl.style.width = w + "px";
          viewerEl.style.height = h + "px";
        }
        // 先尝试 SDK 自带 resize
        if (this._app && this._app.resize) {
          this._app.resize();
        } else if (this._app && this._app._viewer && this._app._viewer.resize) {
          this._app._viewer.resize();
        } else if (this._app && this._app.viewer && this._app.viewer.resize) {
          this._app.viewer.resize();
        }
        // 强制手动调整 canvas 尺寸（兜底）
        const canvas = container.querySelector("canvas");
        if (canvas) {
          canvas.style.width = w + "px";
          canvas.style.height = h + "px";
          canvas.width = Math.floor(w * window.devicePixelRatio);
          canvas.height = Math.floor(h * window.devicePixelRatio);
        }
      } catch (e) {}
    },
    initMap() {
      if (!window.Swqyun || !window.Swqyun.loadScene) {
        this.isInitializing = false;
        console.error("[SwqyunMap] Swqyun SDK not loaded");
        return;
      }
      const el = document.getElementById(this.containerId);
      if (!el) {
        this.isInitializing = false;
        console.error("[SwqyunMap] container element not found:", this.containerId);
        return;
      }
      let sceneUrl = this.sceneUrl;
      // 统一将四维轻云域名替换为本地代理路径，避免生产环境浏览器 CORS 拦截
      if (sceneUrl) {
        if (sceneUrl.startsWith('https://api.swqyun.com')) {
          sceneUrl = sceneUrl.replace('https://api.swqyun.com', '/proxy-swqyun');
        } else if (sceneUrl.startsWith('http://api.swqyun.com')) {
          sceneUrl = sceneUrl.replace('http://api.swqyun.com', '/proxy-swqyun');
        }
      }

      const self = this;
      const sceneOpts = {
        url: sceneUrl,
        session_key: this.sessionKey,
        appid: this.appId,
        container: this.containerId
      };

      // 先尝试新版 loadScene
      window.Swqyun.loadScene(sceneOpts).then(app => {
        self.setupMapApp(app);
      }).catch(err => {
        console.error("[SwqyunMap] new loadScene failed:", err);
        // 新版失败，启动轮询等待 viewer 出现（viewer 可能在后台继续创建）
        self.pollForViewer(err);
      });
    },
    setupMapApp(app) {
      this.isInitializing = false;
      this._app = app;

      window.addEventListener("resize", this.doResize);
      [500, 1000, 2000, 3000].forEach(ms => setTimeout(() => this.doResize(), ms));

      const AppEventType = window.Swqyun.AppEventType;
      const Core = window.Swqyun.Core;

      if (app.event) {
        app.event.on(AppEventType.SCENE_LOAD_SUCCESSED, () => {});
        app.event.on(AppEventType.SCENE_LOAD_FAILED, () => {});
        app.event.on(AppEventType.MAP_STATUS_CHANGED, status => {
          this.currentHeight = status.camera ? status.camera.height : 100;
        });
        // 方案1: scene.postRender — 每帧触发，带 100ms 节流，实现实时同步
        try {
          const viewer = app._viewer || app;
          if (viewer && viewer.scene && viewer.scene.postRender) {
            this._postRenderHandler = () => {
              const now = Date.now();
              if (!this._lastCameraEmit || now - this._lastCameraEmit > 100) {
                const state = this.getCameraState();
                if (state) {
                  this.$emit("camera-changed", state);
                  this._lastCameraEmit = now;
                }
              }
            };
            viewer.scene.postRender.addEventListener(this._postRenderHandler);
            console.log("[SwqyunMap] postRender camera sync listener registered");
          }
        } catch (e) {
          console.error("[SwqyunMap] postRender listener error:", e);
        }
        // 方案2: camera.changed — 备用，降低阈值提高灵敏度
        try {
          const viewer = app._viewer || app;
          if (viewer && viewer.camera && viewer.camera.changed) {
            if (viewer.camera.percentageChanged !== undefined) {
              viewer.camera.percentageChanged = 0.01;
            }
            this._cameraChangedHandler = () => {
              const state = this.getCameraState();
              if (state) {
                this.$emit("camera-changed", state);
              }
            };
            viewer.camera.changed.addEventListener(this._cameraChangedHandler);
          }
        } catch (e) {
          console.error("[SwqyunMap] camera.changed listener error:", e);
        }
        app.event.on(AppEventType.PICK_CHANGED, (node, _unused1, _unused2, eventType) => {
          if (eventType === Core.ScreenSpaceEventType.LEFT_CLICK) {
            if (node) {
              if (node.key && node.key.startsWith("marker-")) {
                const markerId = node.key.replace("marker-", "");
                const marker = this.markerMap[markerId];
                this.$emit("marker-click", marker);
                return;
              }
              // 所有矢量图节点（点/线/面）统一走 vector-click，不弹菜单
              if (node.key && node.key.startsWith(this.keyPrefix)) {
                const vectorId = this.vectorKeyMap[node.key];
                let vectorData = null;
                if (vectorId && this.vectors) {
                  vectorData = this.vectors.find(v => v.id === vectorId);
                }
                this.$emit("vector-click", { key: node.key, id: vectorId, vector: vectorData, node });
                return;
              }
            }
          }
        });
      }

      this.importExtraScenes();

      // 补充 LEFT_CLICK 空白处点击事件（新版 SDK 主路径）
      // 延迟绑定：app._viewer 可能还没准备好，需要用多种方式查找 viewer
      const bindLeftClick = () => {
        let v = app._viewer || app;
        const container = document.getElementById(this.containerId);
        const canvas = container && container.querySelector("canvas");

        // 如果 app._viewer 不存在，尝试多种方式查找 viewer
        if (!v || !v.screenSpaceEventHandler) {
          // 1. 从 app.getDrawHelper().viewer 获取（其他方法中已验证可行）
          try {
            const helper = app.getDrawHelper ? app.getDrawHelper() : null;
            if (helper && helper.viewer && helper.viewer.screenSpaceEventHandler) {
              v = helper.viewer;
            }
          } catch (e) {}
          // 2. 遍历 container 属性
          if (!v || !v.screenSpaceEventHandler) {
            if (container) {
              for (const key in container) {
                try {
                  const val = container[key];
                  if (val && val.scene && val.camera && val.screenSpaceEventHandler) {
                    v = val;
                    break;
                  }
                } catch (e) {}
              }
            }
          }
          // 3. 遍历 window 对象
          if (!v || !v.screenSpaceEventHandler) {
            for (const key in window) {
              try {
                const val = window[key];
                if (val && val.canvas === canvas && val.scene && val.camera && val.screenSpaceEventHandler) {
                  v = val;
                  break;
                }
              } catch (e) {}
            }
          }
          // 4. 尝试从 canvas.__cesiumWidget 获取
          if ((!v || !v.screenSpaceEventHandler) && canvas && canvas.__cesiumWidget) {
            const widget = canvas.__cesiumWidget;
            if (widget.viewer && widget.viewer.screenSpaceEventHandler) {
              v = widget.viewer;
            } else if (widget.screenSpaceEventHandler) {
              v = widget;
            }
          }
          // 5. 尝试从 container 的 cesium-viewer 子元素获取
          if (!v || !v.screenSpaceEventHandler) {
            const cesiumViewer = container && container.querySelector('.cesium-viewer');
            if (cesiumViewer && cesiumViewer.__viewer && cesiumViewer.__viewer.screenSpaceEventHandler) {
              v = cesiumViewer.__viewer;
            }
          }
        }

        if (v && v.screenSpaceEventHandler) {
          v.screenSpaceEventHandler.setInputAction((click) => {
            try {
              // 兼容不同 Cesium 版本：click 可能是 Cartesian2 或 {position: Cartesian2}
              const position = click.position || click;
              if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
                console.warn("[SwqyunMap] invalid click position", click);
                return;
              }
              const pickedObject = v.scene.pick(position);
              let pickedType = null;
              if (pickedObject && pickedObject.id) {
                const key = pickedObject.id.key || pickedObject.id;
                if (key && key.startsWith("marker-")) {
                  pickedType = "marker";
                } else if (key && key.startsWith(this.keyPrefix)) {
                  pickedType = "vector";
                }
              }
              // 只有点击 marker 时不发射 map-click，点击 vector（图斑边界、网格等）也发射 map-click 以便选点
              if (pickedType !== "marker") {
                const cartesian = v.camera.pickEllipsoid(position, v.scene.globe.ellipsoid);
                if (cartesian) {
                  const cartographic = Core.Cartographic.fromCartesian(cartesian);
                  const lon = Core.Math.toDegrees(cartographic.longitude);
                  const lat = Core.Math.toDegrees(cartographic.latitude);
                  console.log("[SwqyunMap] emitting map-click", lon, lat, "pickedType:", pickedType);
                  this.$emit("map-click", { longitude: lon, latitude: lat });
                } else {
                  console.log("[SwqyunMap] pickEllipsoid returned null");
                }
              } else {
                console.log("[SwqyunMap] clicked on", pickedType, ", skipping map-click");
              }
            } catch (err) {
              console.error("[SwqyunMap] LEFT_CLICK map-click error:", err);
            }
          }, Core.ScreenSpaceEventType.LEFT_CLICK);
          console.log("[SwqyunMap] LEFT_CLICK bound successfully on viewer:", v.constructor ? v.constructor.name : typeof v);
        } else {
          console.log("[SwqyunMap] viewer not ready, retry LEFT_CLICK binding in 500ms");
          setTimeout(bindLeftClick, 500);
        }
      };
      bindLeftClick();

      const el = document.getElementById(this.containerId);
      const canvas = el && el.querySelector("canvas");
      this._canvasEl = canvas;
      if (canvas) {
        const viewer = app._viewer || app;
        this._onContextMenu = e => {
          e.preventDefault();
          const rect = canvas.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          try {
            let picked = null;
            if (viewer && viewer.scene) {
              const pickedObject = viewer.scene.pick(new Core.Cartesian2(x, y));
              if (pickedObject && pickedObject.id) {
                const key = pickedObject.id.key || pickedObject.id;
                if (key && key.startsWith("marker-")) {
                  const markerId = key.replace("marker-", "");
                  picked = { type: "marker", id: markerId, marker: this.markerMap[markerId] };
                } else if (key && key.startsWith(this.keyPrefix)) {
                  const vectorId = this.vectorKeyMap[key];
                  picked = { type: "vector", key: key, id: vectorId };
                }
              }
            }
            if (picked) {
              this.$emit("object-right-click", picked, { screenX: e.clientX, screenY: e.clientY });
              return;
            }
            if (viewer && viewer.camera) {
              const cartesian = viewer.camera.pickEllipsoid(new Core.Cartesian2(x, y), viewer.scene.globe.ellipsoid);
              if (cartesian) {
                const cartographic = Core.Cartographic.fromCartesian(cartesian);
                const lon = Core.Math.toDegrees(cartographic.longitude);
                const lat = Core.Math.toDegrees(cartographic.latitude);
                this.$emit("map-right-click", { longitude: lon, latitude: lat, screenX: x, screenY: y });
              }
            }
          } catch (err) {}
        };
        canvas.addEventListener("contextmenu", this._onContextMenu);
      }

      this.reDrawVectors();
      this.drawMarkers();
      // 不再自动启动绘制，等用户点击点/线/面按钮后再启动
    },
    bindManualEvents(viewer) {
      // 防御性初始化，防止降级路径也抛异常
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      this.pointList = this.pointList || [];
      const Core = window.Swqyun.Core;
      window.addEventListener("resize", this.doResize);
      [100, 500, 1000, 2000].forEach(ms => setTimeout(() => this.doResize(), ms));

      viewer.screenSpaceEventHandler.setInputAction((click) => {
        try {
          // 兼容不同 Cesium 版本：click 可能是 Cartesian2 或 {position: Cartesian2}
          const position = click.position || click;
          if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
            console.warn("[SwqyunMap] invalid click position", click);
            return;
          }
          const pickedObject = viewer.scene.pick(position);
          if (pickedObject && pickedObject.id) {
            const key = pickedObject.id.key || pickedObject.id;
            if (typeof key === "string") {
              if (key.startsWith("marker-")) {
                const markerId = key.replace("marker-", "");
                const marker = this.markerMap[markerId];
                this.$emit("marker-click", marker);
                return;
              }
              // 所有矢量图节点（点/线/面）统一走 vector-click，不弹菜单
              if (key.startsWith(this.keyPrefix)) {
                const vectorId = this.vectorKeyMap[key];
                let vectorData = null;
                if (vectorId && this.vectors) {
                  vectorData = this.vectors.find(v => v.id === vectorId);
                }
                this.$emit("vector-click", { key, id: vectorId, vector: vectorData });
                return;
              }
            }
          }
          // 点击空白处：map-click
          const cartesian = viewer.camera.pickEllipsoid(position, viewer.scene.globe.ellipsoid);
          if (cartesian) {
            const cartographic = Core.Cartographic.fromCartesian(cartesian);
            const lon = Core.Math.toDegrees(cartographic.longitude);
            const lat = Core.Math.toDegrees(cartographic.latitude);
            this.$emit("map-click", { longitude: lon, latitude: lat });
          }
        } catch (err) {
          console.error("[SwqyunMap] LEFT_CLICK handler error:", err);
        }
      }, Core.ScreenSpaceEventType.LEFT_CLICK);

      const el = document.getElementById(this.containerId);
      const canvas = el && el.querySelector("canvas");
      this._canvasEl = canvas;
      if (canvas) {
        this._onContextMenu = e => {
          e.preventDefault();
          const rect = canvas.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          try {
            let picked = null;
            const pickedObject = viewer.scene.pick(new Core.Cartesian2(x, y));
            if (pickedObject && pickedObject.id) {
              const key = pickedObject.id.key || pickedObject.id;
              if (key && key.startsWith("marker-")) {
                const markerId = key.replace("marker-", "");
                picked = { type: "marker", id: markerId, marker: this.markerMap[markerId] };
              } else if (key && key.startsWith(this.keyPrefix)) {
                const vectorId = this.vectorKeyMap[key];
                picked = { type: "vector", key: key, id: vectorId };
              }
            }
            if (picked) {
              this.$emit("object-right-click", picked, { screenX: e.clientX, screenY: e.clientY });
              return;
            }
            const cartesian = viewer.camera.pickEllipsoid(new Core.Cartesian2(x, y), viewer.scene.globe.ellipsoid);
            if (cartesian) {
              const cartographic = Core.Cartographic.fromCartesian(cartesian);
              const lon = Core.Math.toDegrees(cartographic.longitude);
              const lat = Core.Math.toDegrees(cartographic.latitude);
              this.$emit("map-right-click", { longitude: lon, latitude: lat, screenX: x, screenY: y });
            }
          } catch (err) {}
        };
        canvas.addEventListener("contextmenu", this._onContextMenu);
      }

      this._app = { _viewer: viewer };
      this.reDrawVectors();
      this.drawMarkers();
      if (this.editable) {
        this.startDraw();
      }
    },
    pollForViewer(err) {
      const self = this;
      let pollCount = 0;
      const maxPolls = 60; // 30 秒

      const timer = setInterval(() => {
        pollCount++;
        const container = document.getElementById(self.containerId);
        const canvas = container && container.querySelector("canvas");

        if (canvas && !self._app) {
          let viewer = null;

          // 1. 遍历 container 属性
          for (const key in container) {
            try {
              const val = container[key];
              if (val && val.scene && val.camera) {
                viewer = val;
                break;
              }
            } catch (e) {}
          }

          // 2. 遍历 window 对象
          if (!viewer) {
            for (const key in window) {
              try {
                const val = window[key];
                if (val && val.canvas === canvas && val.scene && val.camera) {
                  viewer = val;
                  break;
                }
              } catch (e) {}
            }
          }

          // 3. 尝试从 canvas.__cesiumWidget 获取
          if (!viewer && canvas.__cesiumWidget) {
            const widget = canvas.__cesiumWidget;
            if (widget.viewer) {
              viewer = widget.viewer;
            } else if (widget.scene && widget.camera) {
              viewer = widget;
            }
          }

          // 4. 尝试从 container 的 cesium-viewer 子元素获取
          if (!viewer) {
            const cesiumViewer = container.querySelector('.cesium-viewer');
            if (cesiumViewer && cesiumViewer.__viewer) {
              viewer = cesiumViewer.__viewer;
            }
          }

          if (viewer) {
            clearInterval(timer);
            self.bindManualEvents(viewer);
            return;
          }
        }

        if (pollCount >= maxPolls) {
          clearInterval(timer);
          console.error("[SwqyunMap] viewer not detected after 30s polling");
          self.isInitializing = false;
          self.$emit("map-error", err || "viewer not detected after timeout");
          if (err && (typeof err === 'string' && err.includes('超时') || err && err.message && (err.message.includes('超时') || err.message.includes('timeout'))) && self.sceneUrl && !self.sceneUrl.includes('138b92545134d878d5d90ca45490263c')) {
            self.$emit("scene-load-timeout");
          }
        }
      }, 500);
    },
    getKey(i) {
      this.keyIndex += 1;
      let key = `${this.keyPrefix}-${i}-${this.keyIndex}`;
      this.keyList.push(key);
      return key;
    },
    importExtraScenes() {
      if (!this._app || !this.extraScenes || this.extraScenes.length === 0) return;
      if (!window.Swqyun || !window.Swqyun.fetchBySwqyun) {
        console.error("[SwqyunMap] fetchBySwqyun not available");
        return;
      }
      this.extraScenes.forEach(scene => {
        const url = typeof scene === 'string' ? scene : scene.sceneUrl;
        if (!url) return;
        window.Swqyun.fetchBySwqyun({ url })
          .then(res => {
            if (res && res.plugin && res.data) {
              this.importData(res.plugin, res.data);
            }
          })
          .catch(err => {
            console.error("[SwqyunMap] fetchBySwqyun error:", err);
          });
      });
    },
    importData(plugin, options) {
      if (!this._app) {
        return;
      }
      if (typeof this._app.getImporter !== 'function') {
        console.error("[SwqyunMap] importData failed: getImporter is not a function");
        return;
      }
      const key = options.key;
      try {
        // key 立即加入 keyList，避免异步回调时因 clearDrawVectors 已清空而被误删
        if (key != null && !this.keyList.includes(key)) {
          this.keyList.push(key);
        }
        const promise = this._app.getImporter().use(plugin).import(options);
        if (promise && promise.then) {
          promise.catch(err => {
            console.error("[SwqyunMap] importData error:", err);
          });
        }
      } catch (err) {
        console.error("[SwqyunMap] importData exception:", err);
      }
    },
    reDrawVectors() {
      // 防御性初始化
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      this.pointList = this.pointList || [];
      // 只清除绘制的矢量和点，保留 markers
      this.clearDrawVectors();
      if (!this.vectors || this.vectors.length === 0) {
        return;
      }
      let allPointList = [];
      const isSpot = this.scene === "spot";
      for (let vi = 0; vi < this.vectors.length; vi++) {
        const vec = this.vectors[vi];
        if (!vec || !vec.pointList || vec.pointList.length === 0) continue;
        const pointList = vec.pointList.map(x => ({
          ...x,
          vectorIndex: vi,
          position: this.pointToPosition(x.latitude, x.longitude, x.height)
        }));
        const positions = pointList.map(x => x.position);
        const type = vec.calcType || "point";
        const vectorScene = vec.source || this.scene;
        const vectorIsSpot = vectorScene === "spot";
        const baseIndex = allPointList.length;
        const vecId = vec.id || null;
        if (type === "point") {
          if (positions.length > 0) {
            const key = this.getKey(baseIndex);
            if (vecId) this.vectorKeyMap[key] = vecId;
            this.importData("primitive", {
              type: "Point",
              key: key,
              title: "点" + (vi + 1),
              scale: 1,
              image: this.getPointImage(),
              position: positions[0],
              billboardShow: true
            });
          }
          for (let i = 0; i < pointList.length; i++) {
            let point = pointList[i];
            let newKey = this.getKey(baseIndex + i);
            if (vecId) this.vectorKeyMap[newKey] = vecId;
            let title = `点${baseIndex + i + 1}(${point.imageList && point.imageList.length ? point.imageList.length + "图" : "无图片"})`;
            this.importData("primitive", {
              type: "Point",
              key: newKey,
              title: title,
              scale: 1,
              image: this.getPointImage(),
              position: point.position
            });
            allPointList.push(point);
          }
        } else if (type === "line") {
          if (positions.length > 1) {
            const key = this.getKey(baseIndex);
            if (vecId) this.vectorKeyMap[key] = vecId;
            this.importData("primitive", {
              type: "Polyline",
              key: key,
              positions: positions,
              width: 2,
              color: vectorIsSpot
                ? new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0.6, 0.6, 1)
                : new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0, 0, 1)
            });
          }
          allPointList = allPointList.concat(pointList);
        } else if (type === "area") {
          if (positions.length > 2) {
            const key = this.getKey(baseIndex);
            if (vecId) this.vectorKeyMap[key] = vecId;
            this.importData("primitive", {
              type: "Polygon",
              key: key,
              positions: positions,
              width: 2,
              color: vectorIsSpot
                ? new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0.6, 0.6, 0.15)
                : new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0, 0, 0.6)
            });
          }
          allPointList = allPointList.concat(pointList);
        }
      }
      this.pointList = allPointList;
      // 用 _vectorType 保存当前矢量类型，不污染 innerDrawType（避免 el-radio-group 自动选中）
      this._vectorType = this.vectors.length > 0 ? (this.vectors[0].calcType || "point") : "point";
      if (this.autoFly && allPointList.length > 0) {
        setTimeout(() => this.flyToCenter(), 600);
      }
    },
    clearDrawVectors() {
      if (!this._app) return;
      // 防御性初始化：防止 undefined 导致 for...of 抛异常
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      this.pointList = this.pointList || [];
      this.vectorKeyMap = {};
      let viewer = null;
      try {
        const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
        if (helper) viewer = helper.viewer;
      } catch (e) {}
      try {
        const handler = this._app.getSceneTreeHandler ? this._app.getSceneTreeHandler() : null;
        if (handler) {
          for (let key of this.keyList) {
            let node = handler.getNodeByKey(key);
            if (node) {
              try {
                if (viewer && node.delete) node.delete(viewer);
                else if (node.delete) node.delete();
              } catch (e) {}
            }
          }
        }
      } catch (e) {}
      if (viewer && viewer.entities) {
        for (const entity of this._entities) {
          try { viewer.entities.remove(entity); } catch (e) {}
        }
      }
      this._entities = [];
      this.keyList = [];
      // 注意：不清空 pointList，数据由 clearDraw 统一处理
    },
    // 添加预览网格 Polygon（用于植被网格选点预览）
    // coordinates: [[lon, lat], ...] 弧度坐标
    addPreviewPolygon(coordinates, color) {
      if (!this._app || !coordinates || coordinates.length < 3) return null;
      const Core = window.Swqyun.Core;
      let viewer = null;
      try {
        const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
        if (helper) viewer = helper.viewer;
      } catch (e) {}
      if (!viewer || !viewer.entities) return null;
      this._previewEntities = this._previewEntities || [];
      const positions = coordinates.map(c => Core.Cartesian3.fromRadians(c[0], c[1], 0));
      // 闭合多边形
      if (positions.length > 0 && (positions[0].x !== positions[positions.length - 1].x || positions[0].y !== positions[positions.length - 1].y || positions[0].z !== positions[positions.length - 1].z)) {
        positions.push(positions[0]);
      }
      const entity = viewer.entities.add({
        polygon: {
          hierarchy: positions,
          material: color || Core.Color.RED.withAlpha(0.4),
          outline: true,
          outlineColor: Core.Color.RED
        }
      });
      this._previewEntities.push(entity);
      return entity;
    },
    // 添加预览网格线（横竖线组成的网格）
    // lines: [{positions: [Cartesian3, ...], width?}, ...]
    addPreviewGrid(lines, color) {
      console.log("[addPreviewGrid] called, lines:", lines.length, "app:", !!this._app);
      if (!this._app || !lines || lines.length === 0) {
        console.warn("[addPreviewGrid] early return, app:", !!this._app, "lines:", lines ? lines.length : 0);
        return;
      }
      const Core = window.Swqyun.Core;
      let viewer = null;
      try {
        const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
        if (helper) viewer = helper.viewer;
      } catch (e) {}
      if (!viewer || !viewer.entities) {
        console.warn("[addPreviewGrid] viewer not available");
        return;
      }
      this._previewEntities = this._previewEntities || [];
      lines.forEach((line, idx) => {
        try {
          const entity = viewer.entities.add({
            polyline: {
              positions: line.positions,
              width: line.width || 2,
              material: color || Core.Color.RED.withAlpha(0.8)
            }
          });
          this._previewEntities.push(entity);
          console.log("[addPreviewGrid] line", idx, "added, entity id:", entity.id);
        } catch (err) {
          console.error("[addPreviewGrid] failed to add line", idx, err);
        }
      });
      console.log("[addPreviewGrid] total preview entities:", this._previewEntities.length);
    },
    // 清除所有预览网格
    clearPreviewPolygons() {
      if (!this._app) return;
      let viewer = null;
      try {
        const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
        if (helper) viewer = helper.viewer;
      } catch (e) {}
      this._previewEntities = this._previewEntities || [];
      if (viewer && viewer.entities) {
        for (const entity of this._previewEntities) {
          try { viewer.entities.remove(entity); } catch (e) {}
        }
      }
      this._previewEntities = [];
    },
    drawMarkers() {
      if (!this._app) return;
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      // 清除旧 marker keys
      const markerKeys = Object.keys(this.markerMap).map(id => "marker-" + id);
      for (let key of markerKeys) {
        let node = this._app.getSceneTreeHandler().getNodeByKey(key);
        if (node) {
          this._app.getSceneTreeHandler().removeNode(node);
        }
      }
      this.markerMap = {};
      if (!this.markers || this.markers.length === 0) return;
      for (const m of this.markers) {
        if (m.longitude == null || m.latitude == null) continue;
        const position = window.Swqyun.Core.Cartesian3.fromRadians(parseFloat(m.longitude), parseFloat(m.latitude), 0);
        const key = `marker-${m.id}`;
        this.markerMap[m.id] = m;
        const icon = this.getMarkerIcon(m);
        this.importData("primitive", {
          type: "Point",
          key: key,
          title: m.name || m.type,
          scale: 1,
          image: icon,
          position: position,
          billboardShow: true
        });
      }
    },
    getMarkerIcon(marker) {
      try {
        const type = marker.type;
        const canvas = document.createElement("canvas");
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext("2d");
        let color = "#409EFF";
        let text = "●";
        if (type === "PHOTO") { color = "#409EFF"; text = "照"; }
        else if (type === "TABLE") { color = "#67C23A"; text = "表"; }
        else if (type === "REPORT") { color = "#E6A23C"; text = "报"; }
        else if (type === "DRAW") { color = "#F56C6C"; text = "绘"; }
        // 圆形背景
        ctx.beginPath();
        ctx.arc(16, 16, 14, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.stroke();
        // 文字
        ctx.fillStyle = "#fff";
        ctx.font = "bold 11px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        if (type === "PHOTO" && marker.photoCount >= 1) {
          text = marker.photoCount > 9 ? "9+" : marker.photoCount.toString();
        }
        ctx.fillText(text, 16, 16);
        return canvas.toDataURL();
      } catch (e) {
        console.error("[SwqyunMap] getMarkerIcon error:", e, marker);
        return require("@/assets/location.png");
      }
    },
    getPointImage() {
      if (this.scene === "task") {
        const canvas = document.createElement("canvas");
        canvas.width = 28;
        canvas.height = 28;
        const ctx = canvas.getContext("2d");
        ctx.beginPath();
        ctx.arc(14, 14, 12, 0, Math.PI * 2);
        ctx.fillStyle = "#4488ff";
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.font = "bold 10px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("标", 14, 14);
        return canvas.toDataURL();
      }
      return require("@/assets/location.png");
    },
    getCameraState() {
      if (!this._app) return null;
      try {
        const viewer = this._app._viewer || this._app;
        const cam = viewer && viewer.camera;
        if (!cam) return null;
        let lon = null, lat = null;
        if (cam.position) {
          const cartographic = window.Swqyun.Core.Cartographic.fromCartesian(cam.position);
          lon = window.Swqyun.Core.Math.toDegrees(cartographic.longitude);
          lat = window.Swqyun.Core.Math.toDegrees(cartographic.latitude);
        }
        return {
          position: cam.position,
          longitude: lon,
          latitude: lat,
          heading: cam.heading,
          pitch: cam.pitch,
          roll: cam.roll
        };
      } catch (e) {
        return null;
      }
    },
    setCameraState(state) {
      if (!this._app || !state) return;
      try {
        const viewer = this._app._viewer || this._app;
        const cam = viewer && viewer.camera;
        if (!cam) return;
        let destination = state.position;
        // 如果 position 是普通对象（序列化后的），转换为 Cartesian3
        if (destination && typeof destination === "object" && destination.x != null && destination.y != null && destination.z != null && !(destination instanceof window.Swqyun.Core.Cartesian3)) {
          destination = new window.Swqyun.Core.Cartesian3(destination.x, destination.y, destination.z);
        }
        cam.setView({
          destination: destination,
          orientation: {
            heading: state.heading,
            pitch: state.pitch,
            roll: state.roll
          }
        });
      } catch (e) {
        console.error("setCameraState error", e);
      }
    },
    flyToCenter() {
      if (!this._app || this.pointList.length === 0) return;
      try {
        const viewer = this._app._viewer || this._app;
        const cam = viewer && viewer.camera;
        if (!cam) return;
        const lons = this.pointList.map(p => parseFloat(p.longitude));
        const lats = this.pointList.map(p => parseFloat(p.latitude));
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        if (this.pointList.length === 1) {
          cam.flyTo({
            destination: window.Swqyun.Core.Cartesian3.fromRadians(minLon, minLat, 800),
            orientation: { heading: 0, pitch: -90, roll: 0 }
          });
        } else {
          const rect = window.Swqyun.Core.Rectangle.fromRadians(minLon, minLat, maxLon, maxLat);
          cam.flyTo({ destination: rect });
        }
      } catch (e) {
        console.error("flyToCenter error", e);
      }
    },
    // 飞到指定位置（弧度坐标）
    flyToPosition(lon, lat, height) {
      if (!this._app) return;
      try {
        const viewer = this._app._viewer || this._app;
        const cam = viewer && viewer.camera;
        if (!cam) return;
        cam.flyTo({
          destination: window.Swqyun.Core.Cartesian3.fromRadians(lon, lat, height || 200),
          orientation: { heading: 0, pitch: -90, roll: 0 }
        });
      } catch (e) {
        console.error("flyToPosition error", e);
      }
    },
    reDraw(skipClear) {
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      this.pointList = this.pointList || [];
      if (!skipClear) this.clearDrawVectors();
      const positions = this.pointList.map(x => x.position);
      const type = this.innerDrawType || this._vectorType || "point";
      const isSpot = this.scene === "spot";
      if (type === "point") {
        if (positions.length > 0) {
          this.importData("primitive", {
            type: "Point",
            key: this.getKey(-1),
            title: "点1",
            scale: 1,
            image: this.getPointImage(),
            position: positions[0],
            billboardShow: true
          });
        }
        this.drawPointList();
      } else if (type === "line") {
        if (positions.length > 1) {
          this.importData("primitive", {
            type: "Polyline",
            key: this.getKey(-1),
            positions: positions,
            width: 2,
            color: isSpot
              ? new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0.6, 0.6, 1)
              : new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0, 0, 1)
          });
        }
      } else if (type === "area") {
        if (positions.length > 2) {
          this.importData("primitive", {
            type: "Polygon",
            key: this.getKey(-1),
            positions: positions,
            width: 2,
            color: isSpot
              ? new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0.6, 0.6, 0.15)
              : new window.Swqyun.Core.ColorGeometryInstanceAttribute(1, 0, 0, 0.6)
          });
        }
      }
    },
    drawPointList() {
      if (!this._app) return;
      this.keyList = this.keyList || [];
      this._entities = this._entities || [];
      for (let i = 0; i < this.pointList.length; i++) {
        let point = this.pointList[i];
        let newKey = this.getKey(i);
        let title = `点${i + 1}(${point.imageList && point.imageList.length ? point.imageList.length + "图" : "无图片"})`;
        this.importData("primitive", {
          type: "Point",
          key: newKey,
          title: title,
          scale: 1,
          image: this.getPointImage(),
          position: point.position
        });
      }
    },
    startDraw() {
      if (!this._app) return;
      if (this._startDrawTimer) {
        clearTimeout(this._startDrawTimer);
        this._startDrawTimer = null;
      }
      this._startDrawTimer = setTimeout(() => {
        this._startDrawTimer = null;
        if (!this._app) return;
        const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
        const viewer = helper ? helper.viewer : null;
        if (!viewer || !viewer.scene || !viewer.scene.canvas || !viewer.scene.globe) {
          console.warn("[SwqyunMap] viewer not ready, retry in 1s");
          this._startDrawTimer = setTimeout(() => this.startDraw(), 1000);
          return;
        }
        if (this.isDrawing) this.stopDraw();
        try {
          if (this._currentPrimitive && this._currentPrimitive.destroy) {
            this._currentPrimitive.destroy();
          }
        } catch (e) {}
        this._currentPrimitive = null;
        try {
          // 不再清除已有矢量，避免编辑时看不到原有范围
          // clearDrawVectors 会由 reDrawVectors 在 draw-complete 后统一处理
          this.isDrawing = true;
          let type = "Point";
          if (this.innerDrawType === "point") type = "Point";
          else if (this.innerDrawType === "line") type = "Polyline";
          else if (this.innerDrawType === "area") type = "Polygon";
          if (!helper || !helper.startDrawing) {
            console.error("[SwqyunMap] DrawHelper not available");
            this.isDrawing = false;
            return;
          }
          const self = this;
          this._currentPrimitive = helper.startDrawing(type, (primitive, err) => {
            if (err) {
              self.isDrawing = false;
              return;
            }
            self.isDrawing = false;
            let positions = [];
            if (type === "Point") {
              // Swqyun Point primitive 可能用单数 position，兼容两种写法
              if (primitive.position) {
                positions = [primitive.position];
              } else if (primitive.positions) {
                positions = Array.isArray(primitive.positions) ? primitive.positions : [primitive.positions];
              }
            } else {
              positions = primitive.linePositions || [];
            }
            if (!positions || positions.length === 0) {
              console.warn("[SwqyunMap] draw finished but no positions");
              return;
            }
            if (self.innerDrawType === "point") {
              let tmp = self.positionToPoint(positions[0]);
              if (!tmp || tmp.latitude == null || tmp.longitude == null) {
                console.error("[SwqyunMap] invalid point position");
                return;
              }
              self.pointList = [{
                latitude: tmp.latitude.toFixed(16),
                longitude: tmp.longitude.toFixed(16),
                height: self.currentHeight,
                position: positions[0],
                imageList: []
              }];
            } else {
              self.assignPointList(positions);
            }
            self.reDraw();
            self.$emit("draw-complete", {
              calcType: self.innerDrawType,
              pointList: self.pointList
            });
          }, { clampToGround: false, isTmpToMemory: true, ignoreError: true });
        } catch (err) {
          console.error("startDraw error", err);
          this.isDrawing = false;
        }
      }, 300);
    },
    stopDraw(saveResult) {
      this.isDrawing = false;
      if (this._startDrawTimer) {
        clearTimeout(this._startDrawTimer);
        this._startDrawTimer = null;
      }
      if (this._currentPrimitive) {
        try {
          const helper = this._app && this._app.getDrawHelper ? this._app.getDrawHelper() : null;
          if (helper && helper.stopDrawing) helper.stopDrawing();
        } catch (e) { console.error("[SwqyunMap] stopDrawing error:", e); }
        if (saveResult && this.innerDrawType !== "point" && this._currentPrimitive.linePositions && this._currentPrimitive.linePositions.length > 0) {
          try {
            this.assignPointList(this._currentPrimitive.linePositions);
            this.reDraw();
            this.$emit("draw-complete", {
              calcType: this.innerDrawType,
              pointList: this.pointList
            });
          } catch (e) {}
        }
        if (!saveResult) {
          try {
            if (this._currentPrimitive.destroy) this._currentPrimitive.destroy();
          } catch (e) {}
          this._currentPrimitive = null;
        }
      }
    },
    handleStopDraw() {
      this.stopDraw(true);
    },
    clearDraw() {
      // 如果正在绘制，先停止 DrawHelper，避免 stopDrawing 访问已被清理的节点
      if (this.isDrawing) {
        this.stopDraw();
      }
      // 销毁保留的 SDK 原生 primitive
      try {
        if (this._currentPrimitive && this._currentPrimitive.destroy) {
          this._currentPrimitive.destroy();
        }
      } catch (e) {}
      this._currentPrimitive = null;
      this.clearDrawVectors();
      this.pointList = [];
      // 清除 markers
      if (this._app && this.markers) {
        let viewer = null;
        try {
          const helper = this._app.getDrawHelper ? this._app.getDrawHelper() : null;
          if (helper) viewer = helper.viewer;
        } catch (e) {}
        for (const m of this.markers) {
          let node = this._app.getSceneTreeHandler().getNodeByKey("marker-" + m.id);
          if (node) {
            try {
              if (viewer && node.delete) node.delete(viewer);
              else if (node.delete) node.delete();
            } catch (e) {}
          }
        }
      }
      this.markerMap = {};
      this.$emit("draw-cleared");
    },
    onDrawTypeClick() {
      this.$emit("update:drawType", this.innerDrawType);
      if (this._app) {
        this.startDraw();
      }
    },
    pointToPosition(latitude, longitude, height) {
      const lat = parseFloat(latitude);
      const lon = parseFloat(longitude);
      const h = (height != null && !isNaN(height)) ? parseFloat(height) : this.globalHeight;
      return window.Swqyun.Core.Cartesian3.fromRadians(lon, lat, h);
    },
    positionToPoint(position) {
      try {
        return window.Swqyun.Core.Cartographic.fromCartesian(position);
      } catch (e) {
        console.error("[SwqyunMap] positionToPoint error:", e);
        return null;
      }
    },
    calcPolylineLength(positions) {
      if (!this._app || !this._app.measureHelper) return 0;
      let total = 0;
      for (let i = 0; i < positions.length - 1; i++) {
        total += this._app.measureHelper.getDistance(positions[i], positions[i + 1]);
      }
      return total;
    },
    assignPointList(positions) {
      if (!positions || positions.length <= 0) {
        this.pointList = [];
        return;
      }
      this.pointList = positions.map(point => {
        let tmp = this.positionToPoint(point);
        if (!tmp || tmp.latitude == null || tmp.longitude == null) {
          return { latitude: "0", longitude: "0", height: this.currentHeight, position: point, imageList: [] };
        }
        return {
          latitude: tmp.latitude.toFixed(16),
          longitude: tmp.longitude.toFixed(16),
          height: this.currentHeight,
          position: point,
          imageList: []
        };
      });
      // 去重
      let tmp = [];
      this.pointList.forEach(x => {
        if (!tmp.find(y => y.latitude === x.latitude && y.longitude === x.longitude)) {
          tmp.push({ latitude: x.latitude, longitude: x.longitude, height: x.height, position: x.position, imageList: x.imageList });
        }
      });
      this.pointList = tmp;
    },

  }
};
</script>



// WEBPACK FOOTER //
// src/components/SwqyunMap.vue